import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Tabs, Tab, List, ListItem, ListItemText,
} from '@mui/material';
import {
  Business as SuppliersIcon, Visibility, Warning, CheckCircle,
  Cancel, HourglassEmpty, Star,
} from '@mui/icons-material';

const API = '/api/wmc';

const qualColors: Record<string, string> = {
  qualified: '#10B981',
  pending: '#F59E0B',
  conditionally_qualified: '#3B82F6',
  disqualified: '#EF4444',
};

const riskColors: Record<string, string> = {
  critical: '#EF4444',
  major: '#F59E0B',
  minor: '#10B981',
};

interface Supplier {
  id: string;
  supplier_code: string;
  supplier_name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: Record<string, string> | null;
  risk_classification: string;
  qualification_status: string;
  quality_agreement_date: string | null;
  last_audit_date: string | null;
  next_audit_due: string | null;
  audit_type: string | null;
  notes: string | null;
  is_active: boolean;
  material_count: number;
  created_at: string;
}

const WmcSuppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierMaterials, setSupplierMaterials] = useState<any[]>([]);
  const [supplierLots, setSupplierLots] = useState<any[]>([]);
  const [supplierPOs, setSupplierPOs] = useState<any[]>([]);
  const [detailTab, setDetailTab] = useState(0);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/suppliers/`);
      const data = await res.json();
      setSuppliers(data.results || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openDetail = async (sup: Supplier) => {
    setSelectedSupplier(sup);
    setDetailTab(0);
    try {
      const [matsRes, lotsRes, posRes] = await Promise.all([
        fetch(`${API}/suppliers/${sup.id}/materials/`),
        fetch(`${API}/suppliers/${sup.id}/lots/`),
        fetch(`${API}/suppliers/${sup.id}/purchase_orders/`),
      ]);
      setSupplierMaterials(await matsRes.json());
      setSupplierLots(await lotsRes.json());
      setSupplierPOs(await posRes.json());
    } catch (e) { console.error(e); }
  };

  const filtered = suppliers.filter(s =>
    !search ||
    s.supplier_code.toLowerCase().includes(search.toLowerCase()) ||
    s.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SuppliersIcon /> Approved Supplier List
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderLeft: '4px solid #10B981' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Qualified</Typography>
              <Typography variant="h4" color="#10B981">
                {suppliers.filter(s => s.qualification_status === 'qualified').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderLeft: '4px solid #F59E0B' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="h4" color="#F59E0B">
                {suppliers.filter(s => s.qualification_status === 'pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderLeft: '4px solid #EF4444' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Critical Risk</Typography>
              <Typography variant="h4" color="#EF4444">
                {suppliers.filter(s => s.risk_classification === 'critical').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderLeft: '4px solid #3B82F6' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Suppliers</Typography>
              <Typography variant="h4" color="#3B82F6">{suppliers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TextField
        size="small"
        placeholder="Search suppliers..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Materials</TableCell>
              <TableCell>Last Audit</TableCell>
              <TableCell>Next Audit Due</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(sup => (
              <TableRow key={sup.id} hover>
                <TableCell><Typography variant="body2" fontWeight="bold">{sup.supplier_code}</Typography></TableCell>
                <TableCell>{sup.supplier_name}</TableCell>
                <TableCell>
                  {sup.contact_name && <Typography variant="body2">{sup.contact_name}</Typography>}
                  {sup.contact_email && <Typography variant="caption" color="text.secondary">{sup.contact_email}</Typography>}
                </TableCell>
                <TableCell>
                  <Chip
                    label={sup.risk_classification.toUpperCase()}
                    size="small"
                    sx={{ backgroundColor: `${riskColors[sup.risk_classification]}20`, color: riskColors[sup.risk_classification], fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={sup.qualification_status.replace('_', ' ')}
                    size="small"
                    sx={{ backgroundColor: `${qualColors[sup.qualification_status]}20`, color: qualColors[sup.qualification_status], fontWeight: 'bold', textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell align="center">{sup.material_count}</TableCell>
                <TableCell>{sup.last_audit_date || '—'}</TableCell>
                <TableCell>
                  {sup.next_audit_due ? (
                    <Typography variant="body2" sx={{
                      color: new Date(sup.next_audit_due) < new Date() ? '#EF4444' : '#D1D5DB'
                    }}>
                      {sup.next_audit_due}
                    </Typography>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetail(sup)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={!!selectedSupplier} onClose={() => setSelectedSupplier(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          {selectedSupplier?.supplier_name} ({selectedSupplier?.supplier_code})
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
            <Tab label="Info" />
            <Tab label={`Materials (${supplierMaterials.length})`} />
            <Tab label={`Lots (${supplierLots.length})`} />
            <Tab label={`POs (${supplierPOs.length})`} />
          </Tabs>

          {detailTab === 0 && selectedSupplier && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Contact</Typography>
                <Typography>{selectedSupplier.contact_name || '—'}</Typography>
                <Typography variant="body2">{selectedSupplier.contact_email}</Typography>
                <Typography variant="body2">{selectedSupplier.contact_phone}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                {selectedSupplier.address ? (
                  <Typography variant="body2">
                    {selectedSupplier.address.street}<br />
                    {selectedSupplier.address.city}, {selectedSupplier.address.state} {selectedSupplier.address.zip}
                  </Typography>
                ) : <Typography>—</Typography>}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Quality Agreement</Typography>
                <Typography>{selectedSupplier.quality_agreement_date || 'Not on file'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Audit Type</Typography>
                <Typography sx={{ textTransform: 'capitalize' }}>{selectedSupplier.audit_type || '—'}</Typography>
              </Grid>
              {selectedSupplier.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{selectedSupplier.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}

          {detailTab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell>Supplier P/N</TableCell>
                  <TableCell>Lead Time</TableCell>
                  <TableCell>Unit Cost</TableCell>
                  <TableCell>Preferred</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supplierMaterials.map((m: any) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.material_name} ({m.material_code})</TableCell>
                    <TableCell>{m.supplier_part_number || '—'}</TableCell>
                    <TableCell>{m.lead_time_days ? `${m.lead_time_days} days` : '—'}</TableCell>
                    <TableCell>{m.unit_cost ? `$${m.unit_cost}` : '—'}</TableCell>
                    <TableCell>{m.is_preferred ? <Star sx={{ color: '#F59E0B' }} fontSize="small" /> : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {detailTab === 2 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lot #</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Available</TableCell>
                  <TableCell>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supplierLots.map((l: any) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.lot_number}</TableCell>
                    <TableCell>{l.material_name}</TableCell>
                    <TableCell><Chip label={l.status} size="small" /></TableCell>
                    <TableCell>{l.quantity_available} {l.unit}</TableCell>
                    <TableCell>{l.expiry_date || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {detailTab === 3 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PO #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ordered</TableCell>
                  <TableCell>Expected</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supplierPOs.map((po: any) => (
                  <TableRow key={po.id}>
                    <TableCell>{po.po_number}</TableCell>
                    <TableCell><Chip label={po.status} size="small" /></TableCell>
                    <TableCell>{po.ordered_date || '—'}</TableCell>
                    <TableCell>{po.expected_delivery_date || '—'}</TableCell>
                    <TableCell>{po.total_value ? `$${po.total_value}` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setSelectedSupplier(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmcSuppliers;
